// src/ui/LoginPending.jsx — the gate between "the password was right" and "you're in".
//
// A browser that signs in with a password is an UNRECOGNISED DEVICE, and the account's
// rule (App.tsx → verifySessionOnline → LoginPendingScreen on the handset) is that an
// unrecognised device is admitted only by:
//   • a trusted device saying yes — users/{uid}/login_requests/{id} + a push, or
//   • a 6-digit code sent to the account's email address.
// This is the browser's copy of that screen, using the same documents and the same
// endpoints, so the phone's approval sheet cannot tell the difference.

import React, { useEffect, useRef, useState } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { registerDevice } from '../crypto/identity';
import {
  createLoginRequest, watchLoginRequest, sendEmailCode, verifyEmailCode,
  selfApproveRequest, abandonLogin, MSG,
} from '../services/auth';
import { writeSession, deviceLabel, locationLabel } from '../services/session';
import { recordLoginAlert } from '../services/securityAlert';
import BrandLottie from './BrandLottie';
import { Laptop, MapPin, Mail, ShieldCheck, Alert, Check, Back } from './icons';
import { markShared } from '../services/sharedMachine';
import './pairing.css';

// The phone ignores a request older than five minutes, so offer a fresh one just before
// that instead of leaving someone staring at a sheet that will never arrive.
const STALE_MS = 4 * 60 * 1000;

export default function LoginPending({ user, onApproved }) {
  const [req, setReq] = useState(null);          // {id, deviceName, location}
  const [phase, setPhase] = useState('sending'); // sending | waiting | declined | stale | error
  const [mode, setMode] = useState('device');    // device | email
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  /* 🟢 THE QUESTION A GLOBAL IDLE TIMER IS ACTUALLY TRYING TO ASK. Whether this
     machine is the person's own is knowable — by asking them, once, here — and
     asking beats guessing it every fifteen minutes for everybody. A shared
     machine gets the short caps in services/sessionPolicy, no history export,
     and a wipe when the tab goes. */
  const [shared, setShared] = useState(false);
  const doneRef = useRef(false);
  const unsubRef = useRef(null);
  const reqRef = useRef(null);

  /** Shared landing for both routes: publish our key, write the session row, go. */
  const admit = async (trusted, method) => {
    if (doneRef.current) return;
    doneRef.current = true;
    try { await registerDevice(user.uid); } catch { /* sealing will retry */ }
    /* 🟢 marked BEFORE the session write, so nothing that reacts to the write
       can run a beat ahead of knowing what kind of machine this is. */
    markShared(shared);
    await writeSession(user.uid, { trusted, method, shared });

    // 🟥 ONLY the email-code route writes the alert from here. When a phone approves the
    //    login, that phone writes the record itself (services/SecurityAlerts.ts) — doing
    //    it on both sides would put two cards in Note to Self for one login. An emailed
    //    code involves no phone at all, so if this browser doesn't write it, nothing does.
    //    Not awaited: the record must never stand between a correct code and the app.
    if (method === 'email_code') {
      recordLoginAlert(user.uid, { method, location: reqRef.current?.location }).catch(() => {});
    }
    onApproved?.();
  };

  const ask = async () => {
    setPhase('sending'); setErr('');
    try {
      if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
      const r = await createLoginRequest(user.uid);
      reqRef.current = r;
      setReq(r);
      setPhase('waiting');
      unsubRef.current = watchLoginRequest(user.uid, r.id, async (status, data) => {
        if (status === 'approved') {
          // 🟥 trusted is the approver's CHECKBOX, not a formality. Unchecked means "let
          //    it in this once" — we honour that by admitting the session but recording
          //    trusted:false, so the next visit asks again. Same as the handset.
          await admit(data?.trusted === true, 'device_approval');
        } else if (status === 'declined') {
          setPhase('declined');
        }
      });
      setTimeout(() => {
        if (!doneRef.current && reqRef.current?.id === r.id) setPhase((p) => (p === 'waiting' ? 'stale' : p));
      }, STALE_MS);
    } catch {
      setPhase('error');
    }
  };

  useEffect(() => {
    ask();
    return () => { if (unsubRef.current) unsubRef.current(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.uid]);

  const goEmail = async () => {
    setMode('email'); setErr(''); setNote(''); setBusy(true);
    const res = await sendEmailCode(user.email);
    setBusy(false);
    if (!res.ok) setErr(res.error);
    else setNote(`Code sent to ${user.email}.`);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr(''); setBusy(true);
    const res = await verifyEmailCode(user.email, code);
    if (!res.ok) { setBusy(false); setErr(res.error || MSG.codeBad); return; }
    if (reqRef.current?.id) await selfApproveRequest(user.uid, reqRef.current.id);
    await admit(true, 'email_code');
    setBusy(false);
  };

  const cancel = async () => {
    doneRef.current = true;
    await abandonLogin(user.uid, reqRef.current?.id);
  };

  const where = req ? locationLabel(req.location) : 'Locating…';

  return (
    <div className="pair-doc gate-doc">
      <div className="mesh" aria-hidden="true">
        <span className="m1" /><span className="m2" /><span className="m3" />
        <span className="m4" /><span className="m5" /><span className="m6" />
      </div>
      <div className="grain" aria-hidden="true" />

      <div className="gate-wrap">
        <div className="pair-card2 gate-card">
          <div className="gate-mark"><BrandLottie size={132} bare /></div>

          {mode === 'device' ? (
            <>
              <h3>
                {phase === 'declined' ? 'Login declined'
                  : phase === 'error' ? 'Something went wrong'
                    : 'Approve this login'}
              </h3>
              <div className="csub">
                {phase === 'declined'
                  ? 'This device was turned away from your account.'
                  : phase === 'error'
                    ? 'We couldn’t reach your account just now.'
                    : <>Open Allo on a phone you’re already signed in on. It will ask if this is you.</>}
              </div>

              <div className="gate-device">
                <span className="gd-ico"><Laptop width={20} height={20} /></span>
                <div className="gd-txt">
                  <div className="gd-name">{deviceLabel()}</div>
                  <div className="gd-sub"><MapPin width={12} height={12} />{where}</div>
                </div>
                {phase === 'waiting' && <span className="gd-wait" aria-label="Waiting" />}
              </div>

              {phase === 'stale' && (
                <div className="lg-note"><Alert width={15} height={15} />
                  Still waiting. Your phone stops showing a request after a few minutes — send a fresh one.
                </div>
              )}
              {phase === 'error' && err && <div className="lg-err"><Alert width={15} height={15} />{err}</div>}

              {(phase === 'stale' || phase === 'error' || phase === 'declined') && (
                <button className="lg-go" type="button" onClick={ask}>
                  {phase === 'declined' ? 'Ask again' : 'Send a new request'}
                </button>
              )}

              <button className="gate-alt" type="button" onClick={goEmail}>
                <span className="ga-ico"><Mail width={18} height={18} /></span>
                <span className="ga-txt">
                  <b>Use an email code instead</b>
                  <i>We’ll send a 6-digit code to {user.email}</i>
                </span>
              </button>

              <label className="lg-shared">
                <input
                  type="checkbox"
                  checked={shared}
                  onChange={(e) => setShared(e.target.checked)}
                />
                <span className="ls-txt">
                  <b>This is a shared or public computer</b>
                  <i>Signs out after 12 hours, locks after 15 minutes idle, keeps no
                     chat history on this machine, and erases everything when you close the tab.</i>
                </span>
              </label>

              <button className="gate-alt danger" type="button" onClick={cancel}>
                <span className="ga-ico"><Back width={18} height={18} /></span>
                <span className="ga-txt"><b>Cancel</b><i>Return to the login screen</i></span>
              </button>

              <div className="enc-foot"><ShieldCheck width={13} height={13} />
                Approving links this browser with a key of its own
              </div>
            </>
          ) : (
            <form onSubmit={submitCode}>
              <h3>Enter your code</h3>
              <div className="csub">We sent a 6-digit code to {user.email}.</div>

              {err && <div className="lg-err"><Alert width={15} height={15} />{err}</div>}
              {note && <div className="lg-note"><Check width={15} height={15} />{note}</div>}

              <label className="lg-field">
                <span className="lg-label">6-digit code</span>
                <input className="lg-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                  placeholder="123456" value={code} autoFocus
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} />
              </label>

              <button className="lg-go" type="submit" disabled={busy || code.length < 6}>
                {busy ? 'Checking…' : 'Verify and continue'}
              </button>

              <div className="lg-alt">
                <button type="button" className="lg-link" onClick={goEmail} disabled={busy}>Send it again</button>
                {' · '}
                <button type="button" className="lg-link" onClick={() => { setMode('device'); setErr(''); setNote(''); }}>
                  Back to device approval
                </button>
              </div>
            </form>
          )}
        </div>

        <button className="gate-signout" type="button" onClick={() => signOut(auth)}>
          Not you? Sign out
        </button>
      </div>
    </div>
  );
}
